import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, Share, Plus, X } from "lucide-react";

/**
 * Small header CTA that installs the PWA in one tap on Chrome/Edge/Android
 * (uses the captured `beforeinstallprompt` event) and shows an iOS-specific
 * "Add to Home Screen" tutorial for Safari where the browser prompt is
 * unavailable.
 *
 * Hides itself when already running standalone / installed.
 */
export default function InstallAppButton({ testid = "home-nav-install", compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [installed, setInstalled] = useState(false);

  const isIos =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream;

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  useEffect(() => {
    if (isStandalone) {
      setInstalled(true);
      return;
    }
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  if (installed) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const result = await deferred.userChoice;
        if (result.outcome === "accepted") setInstalled(true);
      } catch {
        /* noop */
      }
      setDeferred(null);
      return;
    }
    // Fallback for iOS / any browser without the prompt event.
    setShowIosSheet(true);
  };

  return (
    <>
      <Button
        size="sm"
        onClick={onClick}
        title="Install the app on your phone"
        className="bg-[#00A86B] hover:bg-[#008957] text-white font-bold uppercase tracking-widest"
        data-testid={testid}
      >
        <Smartphone className="w-3.5 h-3.5 mr-1.5" />
        {compact ? "Install" : "Install App"}
      </Button>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="install-app-ios-sheet"
        >
          <div
            className="w-full max-w-md bg-[#0f1a15] border border-[#F4B400]/40 rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#F4B400]/20 border border-[#F4B400]/40 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-[#F4B400]" />
                </div>
                <div>
                  <div className="font-anton text-xl uppercase tracking-tight text-white leading-none">
                    Install Mara League
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    {isIos ? "Safari · iPhone / iPad" : "Add to Home Screen"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowIosSheet(false)}
                className="text-zinc-500 hover:text-white shrink-0"
                aria-label="Close"
                data-testid="install-app-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              {isIos
                ? "Follow these two steps to add the League to your home screen. It'll open full-screen and cache pages so you can browse offline."
                : "Your browser doesn't support one-tap install. On mobile Chrome/Edge, open the menu (⋮) → Add to Home Screen. On desktop, look for the install icon in the address bar."}
            </p>

            {isIos && (
              <ol className="space-y-3 mb-4">
                <li className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/30">
                  <div className="w-8 h-8 rounded-lg bg-[#F4B400]/20 border border-[#F4B400]/40 flex items-center justify-center shrink-0">
                    <Share className="w-4 h-4 text-[#F4B400]" />
                  </div>
                  <div className="text-sm text-white">
                    <b>Tap the Share icon</b>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      Located at the bottom of Safari (a square with an upward arrow).
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/30">
                  <div className="w-8 h-8 rounded-lg bg-[#F4B400]/20 border border-[#F4B400]/40 flex items-center justify-center shrink-0">
                    <Plus className="w-4 h-4 text-[#F4B400]" />
                  </div>
                  <div className="text-sm text-white">
                    <b>Choose "Add to Home Screen"</b>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      Then tap <b>Add</b> in the top-right corner. Done.
                    </div>
                  </div>
                </li>
              </ol>
            )}

            <Button
              onClick={() => setShowIosSheet(false)}
              className="w-full bg-[#F4B400] text-black hover:bg-[#e5aa00] font-bold uppercase tracking-widest"
              data-testid="install-app-got-it"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
